import React, { useState, useEffect } from 'react';
import {
  Chip,
  Stack,
  Typography,
  Collapse,
  IconButton,
  Tooltip,
  styled,
  alpha
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Topic as TopicIcon
} from '@mui/icons-material';

interface Topic {
  id: string;
  name: string;
  confidence?: number;
  category?: string;
}

interface TopicPillsProps {
  messageId: string;
  compact?: boolean;
  maxVisible?: number;
  onTopicClick?: (topic: Topic) => void;
  enabled?: boolean; // Feature flag support
}

const TopicsContainer = styled('div')(({ theme }) => ({
  marginTop: theme.spacing(1),
  marginBottom: theme.spacing(0.5),
}));

const TopicChip = styled(Chip, {
  shouldForwardProp: (prop) => prop !== 'confidence'
})<{ confidence?: number }>(({ theme, confidence = 1 }) => ({
  height: 20,
  fontSize: '0.75rem',
  fontWeight: 500,
  borderRadius: 10,
  cursor: 'pointer',
  backgroundColor: alpha(theme.palette.primary.main, 0.08 + (confidence * 0.04)),
  color: theme.palette.primary.main,
  border: `1px solid ${alpha(theme.palette.primary.main, 0.2 + (confidence * 0.1))}`,
  transition: 'all 0.2s ease-in-out',
  '&:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.12 + (confidence * 0.06)),
    transform: 'translateY(-1px)',
    boxShadow: theme.shadows[2],
  },
  '& .MuiChip-label': {
    padding: '0 6px',
    fontSize: 'inherit',
  },
}));

const ExpandButton = styled(IconButton)(({ theme }) => ({
  width: 24,
  height: 24,
  padding: 2,
  color: theme.palette.text.secondary,
  '&:hover': {
    backgroundColor: alpha(theme.palette.action.hover, 0.05),
  },
}));

const TopicPills: React.FC<TopicPillsProps> = ({
  messageId,
  compact = false,
  maxVisible = 2,
  onTopicClick,
  enabled = true // Default enabled for gradual rollout
}) => {
  const [topics, setTopics] = useState<Topic[]>([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);

  // Fetch topics for this message
  useEffect(() => {
    const fetchTopics = async () => {
      if (!messageId) return;

      setLoading(true);
      try {
        // Make API call to get topics for this message
        const response = await fetch(`/api/v1/topics/message/${messageId}`, {
          credentials: 'include',
        });

        if (response.ok) {
          const data = await response.json();
          setTopics(data.topics || []);
        } else {
          // Silently handle failure - topics are enhancement, not critical
          setTopics([]);
        }
      } catch (error) {
        // Silently handle errors - topics are enhancement, not critical
        console.debug('Topics fetch failed:', error);
        setTopics([]);
      } finally {
        setLoading(false);
      }
    };

    // Small delay to allow message to be fully processed
    const timeoutId = setTimeout(fetchTopics, 1000);
    return () => clearTimeout(timeoutId);
  }, [messageId]);

  const handleTopicClick = (topic: Topic) => {
    if (onTopicClick) {
      onTopicClick(topic);
    } else {
      // Default behavior: filter messages by this topic
      // This could be implemented as a context/store action
      console.log('Topic clicked:', topic);
    }
  };

  // Don't render if feature disabled, no topics, or still loading
  if (!enabled || loading || topics.length === 0) {
    return null;
  }

  const visibleTopics = expanded ? topics : topics.slice(0, maxVisible);
  const hasMoreTopics = topics.length > maxVisible;

  return (
    <TopicsContainer>
      <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" gap={0.5}>
        {!compact && (
          <Tooltip title="Detected topics">
            <TopicIcon
              fontSize="small"
              style={{
                color: 'rgba(0,0,0,0.6)',
                fontSize: '0.875rem',
                marginRight: 4
              }}
            />
          </Tooltip>
        )}

        <Collapse in={true} orientation="horizontal">
          <Stack direction="row" spacing={0.5} flexWrap="wrap" gap={0.5}>
            {visibleTopics.map((topic, index) => (
              <TopicChip
                key={topic.id || `${topic.name}-${index}`}
                label={topic.name}
                size="small"
                confidence={topic.confidence}
                onClick={() => handleTopicClick(topic)}
              />
            ))}
          </Stack>
        </Collapse>

        {hasMoreTopics && (
          <Tooltip title={expanded ? 'Show less' : `Show ${topics.length - maxVisible} more topics`}>
            <ExpandButton
              onClick={() => setExpanded(!expanded)}
              style={{
                transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.2s ease',
              }}
            >
              <ExpandMoreIcon fontSize="small" />
            </ExpandButton>
          </Tooltip>
        )}
      </Stack>

      {expanded && topics.length > maxVisible && (
        <Collapse in={expanded}>
          <Stack
            direction="row"
            spacing={0.5}
            flexWrap="wrap"
            gap={0.5}
            style={{ marginTop: 4, marginLeft: compact ? 0 : 24 }}
          >
            {topics.slice(maxVisible).map((topic, index) => (
              <TopicChip
                key={topic.id || `${topic.name}-${index + maxVisible}`}
                label={topic.name}
                size="small"
                confidence={topic.confidence}
                onClick={() => handleTopicClick(topic)}
              />
            ))}
          </Stack>
        </Collapse>
      )}

      {compact && topics.length > 0 && (
        <Typography
          variant="caption"
          color="text.secondary"
          style={{
            fontSize: '0.7rem',
            marginLeft: 4,
            opacity: 0.7
          }}
        >
          {topics.length} topic{topics.length !== 1 ? 's' : ''}
        </Typography>
      )}
    </TopicsContainer>
  );
};

export default TopicPills;